import appStore from "@/store/modules/appStore";
import { Instant, Duration } from "@js-joda/core";
import { onUnmounted, watch } from "vue";
import router from "@/router/router";
import type { ResponseInterceptor } from "@/app/api";
import api from "@/app/api";
import { debounce } from "lodash";
import type { Route } from "vue-router";
import eventLogger from "@/app/eventlogging/eventLogger";
import environment from "@/environment";
import type { BrewManApiError } from "@/app/errors";

export function usePerformanceMeasurements() {
  const serviceCallsToIgnore = new Set([
    "UserSessionService.tryGetLicenseForCurrentSession",
    "BmTaskService.getBmTasksByFilter",
    "UserService.findUsersMatchingGipIdToken",
  ]);

  let lookForPageLoad = true; // Waiting for loading to start
  let trackPageLoad = false; // Waiting for load to finish
  let routeLoadStartTimestamp: Instant | null = null;
  let routeLoadFinishTimestamp: Instant | null = null;
  let numberOfApiCalls = 0;
  let routeChangeHook: () => void = () => {};

  const performanceMeasurementsInterceptor: ResponseInterceptor = (
    err: BrewManApiError | undefined,
    fetchResponse: Response,
    responseData: any,
    context: {
      userFriendlyServiceName: string;
      preAsyncStackTrace: string;
      requestStartTimeMsSinceEpoch: number;
    }
  ) => {
    if (!serviceCallsToIgnore.has(context.userFriendlyServiceName)) {
      numberOfApiCalls += 1;
    }
    return [err, responseData];
  };

  // We need to search by name as multiple composables will have different instances of the interceptor
  const responseInterceptorExists = api.responseInterceptors.find(
    (interceptors) => interceptors.name === "performanceMeasurementsInterceptor"
  );

  if (!responseInterceptorExists) {
    api.responseInterceptors.push(performanceMeasurementsInterceptor);

    // afterEach returns a function to remove the hook
    routeChangeHook = router.afterEach((to: Route, from: Route) => {
      lookForPageLoad = true;

      // If after 500ms we haven't started loading at all, stop tracking
      setTimeout(() => {
        if (!trackPageLoad) {
          lookForPageLoad = false;
        }
      }, 500);
    });
  }

  const waitToSettleMs = 200;
  const recordPageLoadMetrics = debounce(() => {
    if (environment.performanceMeasurementsEnabled) {
      // While waiting, we were still loading, so skip for now
      if (appStore.state.loading) {
        recordPageLoadMetrics();
        return;
      }

      if (routeLoadStartTimestamp && routeLoadFinishTimestamp) {
        const timeToLoadMs = Duration.between(
          routeLoadStartTimestamp,
          routeLoadFinishTimestamp
        ).toMillis();

        // Somehow our page took negative time to load, or too long to be considered an
        // average connection
        if (timeToLoadMs <= 0 || timeToLoadMs > 20000) {
          return;
        }

        eventLogger.logEvent("page_load", {
          timeToLoadMs,
          numberOfApiCalls,
          routeName: router.currentRoute.name ?? "RouteNameNotFound",
        });
      }

      numberOfApiCalls = 0;
      routeLoadStartTimestamp = null;
    }
  }, waitToSettleMs);

  watch(
    () => appStore.state.loading,
    (isLoading, wasLoading) => {
      // We are now loading, and were expecting it
      if (isLoading && !wasLoading && lookForPageLoad) {
        routeLoadStartTimestamp = Instant.now();
        lookForPageLoad = false;
        trackPageLoad = true;
      }

      // We are no longer loading, but were tracking
      if (!isLoading && wasLoading && trackPageLoad) {
        routeLoadFinishTimestamp = Instant.now();
        lookForPageLoad = false;
        trackPageLoad = false;
        recordPageLoadMetrics();
      }
    }
  );

  // Remove hooks and interceptor
  onUnmounted(() => {
    routeChangeHook();
    api.responseInterceptors = api.responseInterceptors.filter(
      (interceptors) => interceptors === performanceMeasurementsInterceptor
    );
  });
}
